import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import staticMatchesRaw from "../darts-results.json";
import staticRankingsRaw from "../Rankings.json";
import {
  mergeMatches, buildResultRows, rankingsFromRows, playerNamesFrom, earliestResultDate, toIso,
  canonicaliseAll,
} from "./rankings";

// The single source of results for the whole site: the historical JSON files
// plus any tournaments entered through the admin Results screen (Firestore).
//
// Every player name is mapped onto ONE spelling as it is read (so "hunt, Daniel"
// and "Hunt, Daniel" can never become two players), which means a typo in the
// stored data heals itself everywhere — rankings, averages, profiles.

var ResultsContext = createContext(null);

function compute(tournamentsRaw) {
  var data = canonicaliseAll({
    staticMatches: staticMatchesRaw,
    staticRankings: staticRankingsRaw,
    tournaments: tournamentsRaw,
  });
  var tournaments = data.tournaments;
  var matches = mergeMatches(data.staticMatches, tournaments);
  var rows = buildResultRows({ staticRankings: data.staticRankings, staticMatches: data.staticMatches, tournaments: tournaments });
  var today = toIso(new Date());
  var rankings = rankingsFromRows(rows, today);
  var cache = {};
  return {
    matches: matches,
    rankings: rankings,
    rankingsDate: today,
    rankingsAsOf: function(iso) {
      if (!iso || iso >= today) return rankings;
      if (!cache[iso]) cache[iso] = rankingsFromRows(rows, iso);
      return cache[iso];
    },
    earliestDate: earliestResultDate(rows),
    playerNames: playerNamesFrom(matches, rows),
    canon: data.canon,
    tournaments: tournaments,
  };
}

var staticFallback = null;
function getStaticFallback() {
  if (!staticFallback) {
    staticFallback = compute([]);
    staticFallback.loading = false;
  }
  return staticFallback;
}

export function ResultsProvider(props) {
  var [tournaments, setTournaments] = useState([]);
  var [loaded, setLoaded] = useState(false);

  useEffect(function() {
    var unsub = onSnapshot(
      collection(db, "tournamentResults"),
      function(snap) {
        setTournaments(snap.docs.map(function(d) { return { id: d.id, ...d.data() }; }));
        setLoaded(true);
      },
      function() {
        // Rules not published yet / offline: carry on with the historical data.
        setLoaded(true);
      }
    );
    return unsub;
  }, []);

  var value = useMemo(function() {
    var v = compute(tournaments);
    v.loading = !loaded;
    return v;
  }, [tournaments, loaded]);

  return <ResultsContext.Provider value={value}>{props.children}</ResultsContext.Provider>;
}

export function useResults() {
  var value = useContext(ResultsContext);
  return value || getStaticFallback();
}
